import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { isEmpty } from "lodash";

import {
  addNewMessage,
  filterEditMessages,
  getArtieLogo,
  getConversationID,
  getIsMaximized,
  getPriceFilter,
  removeAndAddMessages,
  selectLabelItems,
  selectMessages,
  selectOptionsTypeLogs,
  setIsDisabled,
  setLabelItems,
  setOptionsTypeLogs,
  setReplies,
  updateFaq,
} from "slices";
import MultiInputView from "components/OptionsBox/MultiInputView";
import {
  EXPLORE_MORE_OPTION,
  RETRY_OPTION,
  RETRY_OPTION_REPLIES,
  compMsgKeys,
  defaultEditFormats,
  feedbackKeyOptions,
  formatMessageEdit,
  getNextOptions,
  makeNextOptions,
  optionTypes,
  renderMascot,
  saveConversationFeedback,
} from "utils";
import { Icon } from "components";
import { fetchPropertyData } from "utils/methods/index";
import { setIsFetchManagement } from "slices/replies-slice/index";
import { getPhoneNumberFilter, selectPropertyId, setAPIKey } from "slices/property-slice/index";
import { updateContactMessages } from "slices/messages-slice/index";

const ContactForm = ({ unsatisfied, staticImageData, customSize, isLastBot, isUserMsg, position, mascotSettings }) => {
  const dispatch = useDispatch();

  const propertyId = useSelector(selectPropertyId);
  const artieLogo = useSelector(getArtieLogo);
  const isMaximized = useSelector(getIsMaximized);
  const isPriceFilter = useSelector(getPriceFilter);
  const allMessages = useSelector(selectMessages);
  const conversationID = useSelector(getConversationID);
  const optionsTypeLogs = useSelector(selectOptionsTypeLogs);
  const prevLabel = useSelector(selectLabelItems);
  const isPhoneRequired = useSelector(getPhoneNumberFilter);
  const [showForm, setShowForm] = useState(true);
  const paramObj = { ...prevLabel };

  const onClick = (messages, nextOptionsType, disablePlaceholerMessage, callbackOptions, data = null) => {
    if (nextOptionsType == "Init") {
      formatMessageEdit("Init");

      return;
    }

    if (defaultEditFormats.includes(nextOptionsType)) {
      dispatch(filterEditMessages([]));
    }

    dispatch(addNewMessage(messages));

    if (callbackOptions) {
      const { data, triggerType } = callbackOptions;

      switch (triggerType) {
        case "SAVE_2_LOCAL_STORAGE":
          paramObj[data.name] = data.data;
          dispatch(setLabelItems(paramObj));
          break;

        default:
          break;
      }
    }

    if (nextOptionsType) {
      if (!disablePlaceholerMessage) {
        setTimeout(() => {
          const message = optionTypes[nextOptionsType] || `typing`;
          dispatch(addNewMessage([{ message: message, speaker: "bot", createdAt: new Date().toISOString() }]));
        }, 300);
      }

      // Disable options clicking
      dispatch(setIsDisabled(true));

      if (["LikeTour", "DisLikeTour", "SkipTour", "LikeContact", "DisLikeContact", "SkipContact"].includes(nextOptionsType)) {
        saveConversationFeedback(conversationID, data, feedbackKeyOptions[nextOptionsType]);
        dispatch(filterEditMessages([]));
      }

      const handleNextOptionSuccess = (res) => {
        const { nextReplies, nextBotMessages } = makeNextOptions(nextOptionsType, res.data, res.extraData || undefined);

        let newMessages = nextBotMessages || [];

        if (["unitView"].includes(nextReplies?.renderType)) {
          newMessages = [{ key: compMsgKeys[nextReplies?.renderType] || nextReplies?.renderType, speaker: "comp", replies: nextReplies }];
        }

        if (!disablePlaceholerMessage) {
          setTimeout(() => {
            dispatch(removeAndAddMessages(newMessages));
          }, 300);
        } else {
          dispatch(addNewMessage(newMessages));
        }

        dispatch(setReplies(nextReplies));
        dispatch(setIsDisabled(false));

        if (nextOptionsType !== replies.optionsType) {
          dispatch(setOptionsTypeLogs([...optionsTypeLogs, replies.optionsType]));
        }

        if (!isPriceFilter && ["PriceBrackets"].includes(nextOptionsType)) {
          if (nextReplies.options.length) {
            const { nextOptionsType: type, data: getData } = nextReplies.options[0];

            getNextOptions(type, getData).then((res) => {
              const { nextReplies } = makeNextOptions(type, res.data, res.extraData || undefined);

              dispatch(setReplies(nextReplies));
            });
          }
        }
      };

      getNextOptions(nextOptionsType, data)
        .then((res) => {
          handleNextOptionSuccess(res);
        })
        .catch(async (err) => {
          if (["401 Unauthorized", "API Key is expired!", "API key is Invalid!"].includes(err?.response?.data?.error)) {
            dispatch(setIsFetchManagement(false));

            await fetchPropertyData(propertyId).then((res) => {
              const { api_key } = res.data;
              dispatch(setAPIKey(api_key));
            });

            getNextOptions(nextOptionsType, data)
              .then((res) => {
                handleNextOptionSuccess(res);
              })
              .catch((err) => {
                const errorMessage = [{ message: "flowErrorMessage", speaker: "bot", createdAt: new Date().toISOString() }];

                const findActiveMessage = allMessages?.find((v) => v.optionsType && v.optionsType === "Init");

                const findKey = findActiveMessage?.key || null;

                const replyOptions = [
                  { ...RETRY_OPTION, data, nextOptionsType, message: messages },
                  { ...EXPLORE_MORE_OPTION, data: findKey },
                ];

                const reply = { ...RETRY_OPTION_REPLIES, options: replyOptions };

                if (!disablePlaceholerMessage) {
                  setTimeout(() => {
                    dispatch(removeAndAddMessages(errorMessage));
                  }, 300);
                } else {
                  dispatch(addNewMessage(errorMessage));
                }

                dispatch(setReplies(reply));
              })
              .finally(() => {
                dispatch(setIsFetchManagement(true));
              });
          }
        })
        .finally(() => {
          dispatch(setIsDisabled(false));
          setShowForm(false);
          dispatch(updateContactMessages());
        });
    }
  };

  const replies = {
    checkStorage: true,
    disableResetBox: false,
    options: [
      {
        content: {
          component: "FullWidthInput",
          componentOptions: {
            placeholder: "firstName",
            type: "text",
            label: "firstName",
            officialLabel: "first name",
            validate: "TEXT_ONLY",
            callbackOptions: null,
          },
        },
        message: [],
        nextOptionsType: "PostContactUs",
        key: "first_name",
        isRequired: true,
        event: "h_click",
        disablePlaceholerMessage: false,
        data: null,
      },
      {
        content: {
          component: "FullWidthInput",
          componentOptions: {
            placeholder: "lastName",
            type: "text",
            label: "lastName",
            officialLabel: "last name",
            validate: "TEXT_ONLY",
            callbackOptions: null,
          },
        },
        message: [],
        nextOptionsType: "PostContactUs",
        key: "last_name",
        isRequired: true,
        event: "h_click",
        disablePlaceholerMessage: false,
        data: null,
      },
      {
        content: {
          component: "FullWidthInput",
          componentOptions: {
            placeholder: "email",
            type: "email",
            label: "email",
            officialLabel: "email",
            validate: "EMAIL",
            callbackOptions: null,
          },
        },
        message: [],
        nextOptionsType: "PostContactUs",
        key: "email",
        isRequired: true,
        event: "h_click",
        disablePlaceholerMessage: false,
        data: null,
      },
      {
        content: {
          component: "FullWidthInput",
          componentOptions: {
            placeholder: "phoneNumber",
            type: "text",
            label: "phoneNumber",
            officialLabel: "phone number",
            validate: "PHONE",
            callbackOptions: null,
            isOptional: isPhoneRequired ? false : true,
          },
        },
        nextOptionsType: "PostContactUs",
        message: [],
        key: "phone",
        isRequired: isPhoneRequired ? true : false,
        event: "h_click",
        disablePlaceholerMessage: false,
        data: null,
      },
    ],
    optionsType: "Form4ContactUs",
    title: "Enter First Name and Last Name",
    renderType: "multiInputView",
    bareMinimum: true,
  };

  useEffect(() => {
    !unsatisfied && dispatch(updateFaq(false));
  }, []);

  return (
    <div className="hayley-mt-4">
      <div style={{ overflowWrap: "break-word" }}>
        <div className={`hayley-flex ${isLastBot && (isMaximized ? "hayley-mb-4" : "hayley-mb-3")}`}>
          {isUserMsg ? (
            isMaximized ? (
              <Icon iconType="DefaultUser" classes={"hayley-h-9 hayley-w-9 hayley-object-cover"} />
            ) : (
              ""
            )
          ) : (
            <>
              {isEmpty(artieLogo) && <div className="hayley-h-9 hayley-w-9">{renderMascot(mascotSettings, position, true, customSize, false)}</div>}

              {!isEmpty(artieLogo) && (
                <div className="hayley-z-10 hayley-flex hayley-h-9 hayley-w-9 hayley-items-center hayley-justify-center hayley-rounded-full hayley-border hayley-border-[#EEE]">
                  <img
                    id="staticImage"
                    src={artieLogo.includes("data:image/gif") ? staticImageData : artieLogo}
                    alt=""
                    className={`hayley-h-8 hayley-w-8 hayley-rounded-full hayley-object-cover`}
                  />
                </div>
              )}
            </>
          )}
          <div>
            <div className="hayley-mx-3 hayley-max-w-[725px] hayley-rounded-lg hayley-rounded-tl-none hayley-bg-custom-7 !hayley-px-4 !hayley-py-2 hayley-text-sm hayley-text-secondary">
              {`${
                unsatisfied
                  ? Object.keys(paramObj).length > 0
                    ? "One of our agents will personally respond to your question on following contact details:"
                    : "I’m sorry about that. Please fill in the details below and one of my colleagues will reach out with a response."
                  : Object.keys(paramObj).length > 0
                  ? "One of our agents will personally respond to your question on following contact details:"
                  : "Please fill in the details below and I will get you in touch with the right person."
              }`}
            </div>
            {showForm && (
              <div className="hayley-mx-3 hayley-mt-4">
                <MultiInputView
                  replies={replies}
                  onClick={onClick}
                  onCancel={() => {
                    dispatch(addNewMessage([{ message: "cancel", speaker: "user", createdAt: new Date().toISOString() }]));
                    dispatch(addNewMessage([{ message: "howHelp", speaker: "bot", createdAt: new Date().toISOString() }]));
                    setShowForm(false);
                    dispatch(updateFaq(true));
                    dispatch(updateContactMessages());
                  }}
                />
              </div>
            )}
            {unsatisfied && (
              <div
                className="hayley-mx-3 hayley-mt-4 hayley-max-w-[725px] hayley-rounded-lg hayley-rounded-tl-none hayley-bg-custom-7 !hayley-px-4 !hayley-py-2 hayley-text-sm hayley-text-secondary"
                style={{ overflowWrap: "break-word" }}
              >
                You can continue asking me more about the property while waiting for a response from our agent
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ContactForm;
